import styled, { css } from 'styled-components'

import { fromMd, timingFast, untilMd, zIndexForeground } from '@syconium/little-miss-figgy'

import { swiperCSS } from '../../../../lib/shared-styles'

// Maximum size of the image on the desktop
const DESKTOP_PREVIEW_MAX_WIDTH = '640px'

// Width of the control panel on the right side of the screen that needs to be considered
// to center the preview image in its space
const DESKTOP_EMBROIDERY_OPTIONS_PANEL_WIDTH = '420px'

// Reserved horizontal space so that the preview has space around it
const DESKTOP_PREVIEW_HORIZONTAL_GUTTER = '96px'

// Reserved vertical space for the close button and title in the modal
const DESKTOP_PREVIEW_VERTICAL_RESERVE = '136px'

const desktopPreviewSizeStyles = css`
  /* Keep the preview square, but constrain it by:
   * 1. its intended desktop max width
   * 2. the horizontal space left after the form panel + gutter
   * 3. the available viewport height inside the modal
   */
  width: min(
    ${DESKTOP_PREVIEW_MAX_WIDTH},
    calc(100vw - ${DESKTOP_EMBROIDERY_OPTIONS_PANEL_WIDTH} - ${DESKTOP_PREVIEW_HORIZONTAL_GUTTER}),
    calc(100vh - ${DESKTOP_PREVIEW_VERTICAL_RESERVE})
  );
`

export const PreviewRoot = styled.div`
  position: relative;
  width: 100%;

  ${fromMd} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`

export const PreviewShell = styled.div`
  ${swiperCSS}

  position: relative;
  width: 100%;
  overflow: hidden;
  margin: 0 auto;
  aspect-ratio: 1;

  .swiper,
  .swiper-wrapper,
  .swiper-slide {
    height: 100%;
  }

  ${untilMd} {
    /* On small screens, keep the preview within the viewport width and
     * leave enough vertical room for the rest of the modal content.
     */
    width: 100vw;
  }

  ${fromMd} {
    ${desktopPreviewSizeStyles}
  }
`

export const PreviewCanvas = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0 auto;
`

export const PreviewImageFrame = styled.div`
  container-type: inline-size;
  position: relative;
  width: 100%;
  height: 100%;
`

export const LogoPreviewUnsupportedNotice = styled.div`
  position: absolute;
  left: 50%;
  bottom: ${o => o.theme.spacing(4)};
  transform: translateX(-50%);
  z-index: ${zIndexForeground};
  max-width: 80%;
  padding: ${o => o.theme.spacing(2, 3)};
  border-radius: ${o => o.theme.borderRadius.sm};
  background-color: ${o => o.theme.color.textOverlay.transparent.content.on.fill};
  color: ${o => o.theme.color.textOverlay.transparent.content.on.content};
  text-align: center;
`

const dotsLayoutStyles = css`
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  z-index: ${zIndexForeground};
`

export const DesktopDots = styled.div<{ $withTopMargin?: boolean }>`
  display: none;

  ${fromMd} {
    ${dotsLayoutStyles};
    position: relative;
    margin-top: ${o => (o.$withTopMargin ? o.theme.spacing(6) : 0)};
  }
`

export const MobileDots = styled.div`
  ${dotsLayoutStyles};
  position: absolute;
  right: ${o => o.theme.spacing(5)};
  bottom: ${o => o.theme.spacing(5)};

  ${fromMd} {
    display: none;
  }
`

export const Dot = styled.span<{ $isSelected: boolean }>`
  width: ${o => o.theme.spacing(1)};
  height: ${o => o.theme.spacing(1)};
  border-radius: ${o => o.theme.borderRadius.full};
  background: ${o =>
    o.$isSelected
      ? o.theme.color.stroke.neutral.border.decoration.on.strong
      : o.theme.color.stroke.neutral.border.decoration.on.background};
`

export const DesktopArrowControls = styled.div`
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: space-between;
  z-index: ${zIndexForeground};
  pointer-events: none;

  ${fromMd} {
    display: flex;
  }
`

export const DesktopArrowButton = styled.button`
  width: ${o => o.theme.spacing(11)};
  height: ${o => o.theme.spacing(11)};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: ${o => o.theme.borderRadius.full};
  background: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary.on.background};
  cursor: pointer;
  pointer-events: auto;
  transition:
    background-color ${timingFast} ease-out,
    color ${timingFast} ease-out;

  > svg path {
    transform-box: fill-box;
    transform-origin: center;
  }

  ${fromMd} {
    &:hover {
      background: ${o => o.theme.color.button.secondary.hover.fill.on.background};
      color: ${o => o.theme.color.button.secondary.hover.content.on.background};
    }

    &:hover > svg path {
      transform: scaleX(1.15);
    }
  }
`
